// Hinweisband „eingeschränkte Verfügbarkeit" — steht auf jeder Seite der
// Website, solange die Praxis die Online-Buchung wegen Urlaub/Betriebsferien
// gesperrt hat.
//
// Bewusst NICHT wegklickbar: die Information entscheidet darüber, ob jemand
// vergeblich einen Termin sucht. Sie ist dafür kompakt gehalten und wiederholt
// sich in der Buchungsstrecke (siehe ClosureNotice).
//
// Das Band hängt im fixen Header (siehe Header.tsx), damit es nicht — wie
// zuvor im normalen Seitenfluss — hinter ihm verschwindet. Weil der Header die
// Seite überlagert und jede Seite ihren Abstand nach oben fest eingebaut hat,
// meldet das Band seine Höhe als CSS-Variable `--closure-banner-h`; App.tsx
// schiebt den Seiteninhalt um genau diesen Betrag nach unten.

import React, { useLayoutEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useBookingRules } from '../hooks/useBookingRules';
import { formatLongDate } from '../utils/formatLongDate';
import { PHONE_DISPLAY, PHONE_HREF, WHATSAPP_HREF } from '../config/contact';

export const BANNER_HEIGHT_VAR = '--closure-banner-h';

const setBannerHeight = (px: number) => {
  document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, `${Math.round(px)}px`);
};

const ClosureBanner: React.FC = () => {
  const { translations, language } = useLanguage();
  const { rules } = useBookingRules();
  const ref = useRef<HTMLDivElement>(null);

  const closure = rules?.closure;
  const t = translations?.closure;
  const visible = Boolean(closure?.active && closure?.until && t);

  // Höhe an die Seite melden — auch bei Umbruch (Drehen des Handys, Sprach-
  // wechsel mit längerem Text). Beim Verschwinden wieder auf 0.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!visible || !el) {
      setBannerHeight(0);
      return;
    }
    setBannerHeight(el.getBoundingClientRect().height);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setBannerHeight(el.getBoundingClientRect().height));
    ro.observe(el);
    return () => {
      ro.disconnect();
      setBannerHeight(0);
    };
  }, [visible, language]);

  if (!visible || !closure || !t) return null;
  const date = formatLongDate(closure.until, language);

  return (
    <div
      ref={ref}
      role="status"
      className="bg-coral/10 border-t border-b border-coral/30 print:hidden"
      data-testid="closure-banner"
    >
      <div className="container mx-auto px-4 py-1.5 sm:py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
        <div className="flex items-start gap-2.5 sm:gap-3 flex-1 min-w-0">
          <svg
            className="hidden sm:block w-5 h-5 flex-shrink-0 text-coral"
            fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {/* Mobil eine schmale Statuszeile mit Kurztext, ab Tablet der volle
              Satz — das Band soll informieren, nicht den Bildschirm füllen. */}
          <p className="text-xs sm:text-sm text-navy leading-snug sm:leading-relaxed">
            <span className="font-bold hidden sm:inline">{t.bannerTitle}</span>{' '}
            <span className="text-navy font-medium sm:font-normal sm:text-navy/80 sm:hidden">{(t.bannerShort || t.bannerText).replace('{date}', date)}</span>
            <span className="text-navy/80 hidden sm:inline">{t.bannerText.replace('{date}', date)}</span>
          </p>
        </div>

        {/* Auf dem Handy stehen Telefon und WhatsApp schon als Icons direkt
            darüber im Header — die Knöpfe hier würden das Band nur um eine
            weitere Zeile höher machen, auf einem Bildschirm, der ohnehin
            knapp ist. */}
        <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
          <a
            href={PHONE_HREF}
            className="px-3 py-1.5 rounded-lg bg-coral text-white text-[13px] sm:text-sm font-semibold hover:bg-coral-dark transition-colors whitespace-nowrap"
          >
            {t.call} {PHONE_DISPLAY}
          </a>
          <a
            href={WHATSAPP_HREF}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 rounded-lg border border-coral/40 text-navy text-[13px] sm:text-sm font-semibold hover:bg-coral/10 transition-colors whitespace-nowrap"
          >
            {t.whatsapp}
          </a>
        </div>
      </div>
    </div>
  );
};

export default ClosureBanner;
